import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Clock, CheckCircle, Search, Filter, BarChart3 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScreeningCard } from "./ScreeningCard";
import { extractCandidateName } from "@/utils/screeningNameExtraction";

interface ScreeningSession {
  id: string;
  candidate_id: string;
  session_status: string;
  transcript: string;
  screening_results: any;
  duration_seconds: number;
  created_at: string;
  completed_at?: string;
  is_inbound_call: boolean;
  candidate_lookup_query?: string;
  audio_url?: string;
  phone_number?: string;
  notes?: string;
  source?: 'voice_session' | 'call_recording';
  ai_summary?: any;
  candidates?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    candidate_number: string;
  };
}

const ScreeningDashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['screening-sessions'],
    queryFn: async () => {
      // Fetch voice screening sessions
      const { data: voiceSessions, error: voiceError } = await supabase
        .from('voice_screening_sessions')
        .select(`
          id,
          candidate_id,
          session_status,
          transcript,
          screening_results,
          duration_seconds,
          created_at,
          completed_at,
          is_inbound_call,
          candidate_lookup_query,
          audio_url,
          ai_summary,
          candidates:candidate_id (
            id,
            first_name,
            last_name,
            email,
            candidate_number
          )
        `)
        .order('created_at', { ascending: false })
        .limit(25);

      // Fetch call recordings
      const { data: callRecordings, error: callError } = await supabase
        .from('call_recordings')
        .select(`
          id,
          recording_url,
          transcript,
          phone_number,
          notes,
          created_at,
          candidate_id,
          candidates:candidate_id (
            id,
            first_name,
            last_name,
            email,
            candidate_number
          )
        `)
        .order('created_at', { ascending: false })
        .limit(25);

      if (voiceError && callError) {
        throw new Error(`Failed to fetch screening data: ${voiceError.message} | ${callError.message}`);
      }

      // Transform call recordings to match voice session format
      const transformedCallRecordings = (callRecordings || []).map(recording => ({
        id: recording.id,
        candidate_id: recording.candidate_id,
        session_status: 'completed', // Call recordings are always completed
        transcript: recording.transcript,
        screening_results: null,
        duration_seconds: 0, // Duration not available for call recordings
        created_at: recording.created_at,
        completed_at: recording.created_at,
        is_inbound_call: true,
        candidate_lookup_query: null,
        audio_url: recording.recording_url,
        phone_number: recording.phone_number,
        notes: recording.notes,
        candidates: recording.candidates,
        source: 'call_recording' // Add source identifier
      }));

      // Combine and sort by created_at
      const allSessions = [
        ...(voiceSessions || []).map(session => ({ ...session, source: 'voice_session' })),
        ...transformedCallRecordings
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
       .slice(0, 50); // Limit total results

      return allSessions as ScreeningSession[];
    }
  });

  const { data: stats } = useQuery({
    queryKey: ['screening-stats'],
    queryFn: async () => {
      // Get stats from both tables
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      
      const { data: voiceStats, error: voiceError } = await supabase
        .from('voice_screening_sessions')
        .select('session_status, duration_seconds, created_at')
        .gte('created_at', thirtyDaysAgo);

      const { data: callStats, error: callError } = await supabase
        .from('call_recordings')
        .select('created_at')
        .gte('created_at', thirtyDaysAgo);

      if (voiceError || callError) {
        console.error('Error fetching stats:', { voiceError, callError });
      }

      const voiceData = voiceStats || [];
      const callData = callStats || [];
      
      const total = voiceData.length + callData.length;
      const completed = voiceData.filter(s => s.session_status === 'completed').length + callData.length; // All call recordings are completed
      const avgDuration = voiceData.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) / (voiceData.length || 1);
      
      return { total, completed, avgDuration, completionRate: total > 0 ? (completed / total * 100) : 0 };
    }
  });

  const filteredSessions = sessions?.filter(session => {
    const candidateName = extractCandidateName(session);
    
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === "" || 
      candidateName.toLowerCase().includes(searchLower) ||
      session.candidates?.email?.toLowerCase().includes(searchLower) ||
      session.candidates?.candidate_number?.toLowerCase().includes(searchLower) ||
      session.candidate_lookup_query?.toLowerCase().includes(searchLower) ||
      session.phone_number?.toLowerCase().includes(searchLower) ||
      session.transcript?.toLowerCase().includes(searchLower) ||
      session.id.toLowerCase().includes(searchLower);
    
    const matchesStatus = statusFilter === "all" || session.session_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Screenings</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <p className="text-xs text-muted-foreground">Last 30 days</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.completed || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.completionRate?.toFixed(1) || 0}% completion rate
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round((stats?.avgDuration || 0) / 60)}m
            </div>
            <p className="text-xs text-muted-foreground">Per screening</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.completionRate?.toFixed(0) || 0}%</div>
            <p className="text-xs text-muted-foreground">Completion rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Screenings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name, email, phone, transcript, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sessions List */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8">Loading screenings...</div>
            ) : filteredSessions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No screening sessions found.
              </div>
            ) : (
              filteredSessions?.map((session) => (
                <ScreeningCard key={session.id} session={session} />
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScreeningDashboard;